import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { Paperclip, Trash2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/cn";
import { Badge } from "@/components/Badge";
import { FileUpload } from "@/components/FileUpload";

export interface RelatedDocument {
  id: string;
  kind: string;
  title: string;
  fileUrl: string;
  createdAt: string;
  uploadedBy?: { firstName: string; lastName: string } | null;
}

/**
 * Documents kept with a record (a meeting, a resolution…): each one typed, uploaded
 * as a file or pasted as a link, listed with who added it and when. The API endpoint
 * decides who may add (board/admin) and who may remove (admin).
 */
export function RelatedDocuments({
  endpoint,
  items,
  kinds,
  kindLabel,
  defaultKind,
  folder,
  canEdit,
  canDelete,
  onChanged,
  emptyText,
  highlightKinds = [],
  noteForKind,
}: {
  /** e.g. `/meetings/${id}/attachments` — POST to add, DELETE `${endpoint}/${attachmentId}` to remove. */
  endpoint: string;
  items: RelatedDocument[];
  kinds: readonly string[];
  kindLabel: (kind: string) => string;
  defaultKind: string;
  folder: string;
  canEdit: boolean;
  canDelete: boolean;
  onChanged: () => void;
  emptyText: string;
  /** Kinds shown with a green badge (the acta, the signed resolution…). */
  highlightKinds?: readonly string[];
  /** Optional hint shown under the form for the selected kind. */
  noteForKind?: (kind: string) => string | null;
}) {
  const { t, i18n } = useTranslation();
  const [kind, setKind] = useState(defaultKind);
  const [title, setTitle] = useState("");
  const [link, setLink] = useState("");
  const [error, setError] = useState<string | null>(null);

  // A title is required by the API, but making the user type one before they can
  // save was a silent trap: the button just stayed disabled with no explanation
  // (reported when pasting a Fireflies link and leaving "Título" blank). Default
  // it to the chosen kind's label, same as the meeting-creation attachment field.
  const effectiveTitle = () => title.trim() || kindLabel(kind);
  const addMutation = useMutation({
    mutationFn: async () => (await api.post(endpoint, { kind, title: effectiveTitle(), fileUrl: link.trim() })).data,
    onSuccess: () => {
      setTitle("");
      setLink("");
      setError(null);
      onChanged();
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? t("common.error")),
  });
  const deleteMutation = useMutation({
    mutationFn: async (attachmentId: string) => (await api.delete(`${endpoint}/${attachmentId}`)).data,
    onSuccess: onChanged,
    onError: (e: any) => alert(e?.response?.data?.message ?? "Error"),
  });
  const note = noteForKind?.(kind) ?? null;

  return (
    <div>
      {items.length === 0 ? (
        <p className="text-sm text-slate-500">{emptyText}</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((a) => (
            <li key={a.id} className="py-2 flex items-center gap-3 text-sm">
              <Badge variant={highlightKinds.includes(a.kind) ? "success" : "info"}>{kindLabel(a.kind)}</Badge>
              <a href={a.fileUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1 flex-1 min-w-0 truncate">
                {a.title} <ExternalLink size={12} />
              </a>
              <span className="text-xs text-slate-500 whitespace-nowrap">
                {a.uploadedBy ? `${a.uploadedBy.firstName} ${a.uploadedBy.lastName} · ` : ""}
                {formatDate(a.createdAt, i18n.language)}
              </span>
              {canDelete && (
                <button
                  type="button"
                  className="text-red-600 hover:text-red-700"
                  title={t("common.delete")}
                  onClick={() => {
                    if (confirm(t("meetings.deleteAttachmentConfirm"))) deleteMutation.mutate(a.id);
                  }}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit && (
        // Not a <form>: this block also lives inside the meeting modal's own form,
        // and nested forms are invalid HTML. Enter in a field adds the document.
        <div
          className="mt-4 border-t border-slate-100 pt-4 space-y-3"
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.target as HTMLElement).tagName === "INPUT") {
              e.preventDefault();
              if (link.trim() && !addMutation.isPending) addMutation.mutate();
            }
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="label">{t("meetings.attachmentKindLabel")}</label>
              <select className="input" value={kind} onChange={(e) => setKind(e.target.value)}>
                {kinds.map((k) => (
                  <option key={k} value={k}>
                    {kindLabel(k)}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="label">{t("meetings.attachmentTitle")}</label>
              <input
                className="input"
                placeholder={kindLabel(kind)}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              <p className="text-xs text-slate-400 mt-1">{t("meetings.attachmentTitleHint")}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
            <div>
              <label className="label">{t("meetings.attachmentLink")}</label>
              <input type="url" className="input" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…" />
            </div>
            <FileUpload label={t("meetings.attachmentOr")} folder={folder} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.heic,.webp" value={link} onUploaded={(url) => setLink(url)} />
          </div>
          {note && <p className="text-xs text-slate-500">{note}</p>}
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
          <div className="flex justify-end">
            <button type="button" className="btn-primary" disabled={!link.trim() || addMutation.isPending} onClick={() => addMutation.mutate()}>
              <Paperclip size={14} /> {t("meetings.addAttachment")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
